import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'
import type { Milestone } from '@naya/shared'

import { useOwnerSession } from '../hooks/useOwnerSession.js'
import { useBranches } from '../hooks/useBranches.js'
import { usePrograms } from '../program/hooks/useProgram.js'
import { logoSrc } from '../logoSrc.js'
import addToGoogleWalletButton from '../../enroll/assets/add-to-google-wallet.svg'
import addToAppleWalletButton from '../../enroll/assets/add-to-apple-wallet.svg'
import './posterPage.css'

const QR_DOWNLOAD_PX = 1024
const WALLET_BUTTON_DOWNLOAD_PX = 1200

export function PosterPage() {
  const { id } = useParams<{ id: string }>()
  const { data: me } = useOwnerSession()
  const branches = useBranches()
  const programsQ = usePrograms()
  const qrWrapRef = useRef<HTMLDivElement>(null)

  // The poster QR is per-branch (it encodes that branch's enroll URL). Reachable
  // at /owner/poster (nav item) or the legacy /owner/branches/:id/poster.
  // Selection precedence: explicit picker > URL :id > first active branch.
  const activeBranches = (branches.data?.branches ?? []).filter(
    (b) => !b.archived_at,
  )
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null)
  const multiBranch = activeBranches.length > 1
  const branch =
    activeBranches.find((b) => b.id === selectedBranchId) ??
    activeBranches.find((b) => b.id === id) ??
    activeBranches[0]

  // Active loyalty programs + the tenant default. When there's more than one,
  // the owner picks which program's poster this is (?p=); the design, rewards
  // and brand color all follow the selected program.
  const activePrograms = (programsQ.data?.programs ?? []).filter(
    (p) => p.status === 'active' && p.pass_type === 'loyalty',
  )
  const defaultProgram =
    activePrograms.find((p) => p.is_default) ?? activePrograms[0]
  const multiProgram = activePrograms.length > 1
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null)
  const selectedProgram =
    activePrograms.find((p) => p.id === selectedProgramId) ?? defaultProgram

  // Short public_code on the QR/link (?p=100001), only when there's a choice;
  // single-program posters keep the bare branch URL. Falls back to the id for
  // any program predating the 0031 backfill.
  const enrollCode = selectedProgram?.public_code ?? selectedProgram?.id ?? null
  const enrollUrl = useMemo(() => {
    if (!branch) return ''
    const base = `${window.location.origin}/e/${branch.slug}`
    return multiProgram && enrollCode ? `${base}?p=${enrollCode}` : base
  }, [branch, multiProgram, enrollCode])

  if (!me || branches.isLoading || programsQ.isLoading) {
    return (
      <div className="poster-loading">
        <p>Loading…</p>
      </div>
    )
  }

  if (!branch) {
    return (
      <div className="poster-missing">
        <h1>No branch yet</h1>
        <p>Add a branch first, then come back to print your poster.</p>
        <Link to="/owner/branches" className="owner-btn">Go to Branches</Link>
      </div>
    )
  }

  const tenant = me.tenant
  const logo = logoSrc(tenant.logo_url, me.default_logo_url, tenant.logo_updated_at)

  const activeProgram = selectedProgram ?? null
  const rewards =
    activeProgram && activeProgram.total_stamps != null
      ? extractRewards(activeProgram.milestones, activeProgram.total_stamps)
      : null
  const brand = activeProgram?.pass_design.primary_color ?? '#b04030'
  const brandText = textOn(brand)

  // Filename-safe stem reused by every download — branch.slug is DB-CHECKed
  // to lowercase alphanumeric+hyphens.
  const downloadStem = `${tenant.slug || 'naya'}-${branch.slug}`

  function copyUrl() {
    if (!navigator.clipboard?.writeText) {
      toast.error('Clipboard not available. Copy the URL manually.')
      return
    }
    void navigator.clipboard
      .writeText(enrollUrl)
      .then(() => toast.success('Enrollment URL copied'))
      .catch(() => toast.error('Copy failed'))
  }

  function downloadQrPng() {
    const svg = qrWrapRef.current?.querySelector('svg')
    if (!svg) {
      toast.error('QR not ready')
      return
    }
    svgToPng(
      svg,
      QR_DOWNLOAD_PX,
      QR_DOWNLOAD_PX,
      `${downloadStem}-qr.png`,
      '#ffffff',
    )
  }

  // Pulls an official Add-to-Wallet badge (bundled SVG) and rasterizes it to a
  // print/share-ready PNG. Same canvas trick as the QR — re-fetches the bundled
  // asset so we don't depend on a DOM node being in the tree. Shared by the
  // Apple + Google badge downloads; height is derived from each SVG's viewBox so
  // the aspect ratio is preserved.
  function downloadWalletBadgePng(asset: string, filename: string) {
    fetch(asset)
      .then((r) => r.text())
      .then((xml) => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(xml, 'image/svg+xml')
        const svg = doc.querySelector('svg')
        if (!svg) throw new Error('SVG parse failed')
        const width = WALLET_BUTTON_DOWNLOAD_PX
        const vb = svg.getAttribute('viewBox')?.split(/\s+/).map(Number)
        const ratio = vb && vb.length === 4 && vb[3] && vb[2] ? vb[3] / vb[2] : 0.2
        const height = Math.round(width * ratio)
        svgToPng(svg, width, height, filename, null)
      })
      .catch(() => toast.error('Could not export the wallet button.'))
  }

  return (
    <div className="poster-root">
      <div className="poster-toolbar">
        <Link to="/dashboard" className="owner-btn">← Back</Link>
        {multiBranch && (
          <label className="poster-program-picker">
            <span>Branch</span>
            <select
              className="owner-input"
              value={branch.id}
              onChange={(e) => setSelectedBranchId(e.target.value)}
            >
              {activeBranches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {multiProgram && (
          <label className="poster-program-picker">
            <span>Program</span>
            <select
              className="owner-input"
              value={selectedProgram?.id ?? ''}
              onChange={(e) => setSelectedProgramId(e.target.value)}
            >
              {activePrograms.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.is_default ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="poster-toolbar-actions">
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={() => window.print()}
          >
            Print poster
          </button>
          <button type="button" className="owner-btn" onClick={copyUrl}>
            Copy URL
          </button>
          <div className="poster-toolbar-downloads">
            <span className="poster-toolbar-downloads-label">Download</span>
            <button type="button" className="owner-btn owner-btn-sm" onClick={downloadQrPng}>
              QR
            </button>
            <button
              type="button"
              className="owner-btn owner-btn-sm"
              onClick={() =>
                downloadWalletBadgePng(addToAppleWalletButton, 'add-to-apple-wallet.png')
              }
            >
              Apple badge
            </button>
            <button
              type="button"
              className="owner-btn owner-btn-sm"
              onClick={() =>
                downloadWalletBadgePng(addToGoogleWalletButton, 'add-to-google-wallet.png')
              }
            >
              Google badge
            </button>
          </div>
        </div>
      </div>

      <p className="poster-toolbar-hint">
        Print the full poster, or download just the QR / wallet badges to design
        your own marketing.
      </p>

      <article className="poster-sheet" aria-label="Enrollment poster">
        <div
          className="poster-top-banner"
          style={{ background: brand, color: brandText }}
        >
          Digital loyalty card
        </div>

        <header className="poster-header">
          <img src={logo} alt="" className="poster-logo" />
          <h1 className="poster-business">{tenant.name}</h1>
          {multiProgram && selectedProgram ? (
            <p className="poster-branch">{selectedProgram.name}</p>
          ) : (
            branches.data &&
            branches.data.branches.length > 1 && (
              <p className="poster-branch">{branch.name}</p>
            )
          )}
        </header>

        {rewards ? (
          <section className="poster-hero-promise" aria-label="Reward summary">
            {needsFreePrefix(rewards.grand) && (
              <p className="poster-hero-kicker">FREE</p>
            )}
            <p className="poster-hero-prize" style={{ color: brand }}>
              {rewards.grand}
            </p>
            <p className="poster-hero-after">
              after {rewards.totalStamps} stamps
            </p>
          </section>
        ) : (
          <section className="poster-hero-promise">
            <h2 className="poster-hero-line">Get your loyalty card</h2>
            <p className="poster-hero-sub">
              Scan to start collecting stamps on your phone.
            </p>
          </section>
        )}

        <p className="poster-scan-cta" style={{ color: brand }}>
          Scan to join ↓
        </p>

        <div className="poster-qr" ref={qrWrapRef}>
          <QRCodeSVG value={enrollUrl} size={360} level="M" />
        </div>

        <p className="poster-url" aria-label="Enrollment URL">
          {enrollUrl}
        </p>

        <div className="poster-wallet-buttons">
          <img
            src={addToAppleWalletButton}
            alt="Add to Apple Wallet"
            className="poster-wallet-button"
          />
          <img
            src={addToGoogleWalletButton}
            alt="Add to Google Wallet"
            className="poster-wallet-button"
          />
        </div>

        <p className="poster-trust-line">
          Free to join · works on Apple Wallet (iPhone) and Google Wallet (Android)
        </p>

        <ol className="poster-steps">
          <li>
            <span className="poster-step-n">1</span>
            <span>Open your phone camera and point it at the QR code.</span>
          </li>
          <li>
            <span className="poster-step-n">2</span>
            <span>Tap the link to enter your details.</span>
          </li>
          <li>
            <span className="poster-step-n">3</span>
            <span>Tap “Add to Wallet” — done!</span>
          </li>
        </ol>

        {rewards && rewards.intermediates.length > 0 && (
          <section className="poster-rewards" aria-label="Reward ladder">
            <h3 className="poster-rewards-title" style={{ color: brand }}>
              What you’ll earn
            </h3>
            <ul className="poster-rewards-list">
              {rewards.intermediates.map((m) => (
                <li key={m.at_stamp} className="poster-reward-row">
                  <span
                    className="poster-reward-stamp"
                    style={{ color: brand }}
                  >
                    Stamp {m.at_stamp}
                  </span>
                  <span className="poster-reward-dots" aria-hidden="true" />
                  <span className="poster-reward-text">{m.reward_text}</span>
                </li>
              ))}
            </ul>
            <div
              className="poster-reward-grand"
              style={{ borderColor: brand }}
            >
              <span
                className="poster-reward-grand-label"
                style={{ background: brand, color: brandText }}
              >
                ★ Grand prize · stamp {rewards.totalStamps}
              </span>
              <span className="poster-reward-grand-text">{rewards.grand}</span>
            </div>
          </section>
        )}

        {!activeProgram && (
          <p className="poster-rewards-fallback">
            Set up your loyalty program to show rewards here.
          </p>
        )}

        <footer className="poster-footer">
          <span>Powered by Naya Pass</span>
        </footer>
      </article>
    </div>
  )
}

interface ExtractedRewards {
  totalStamps: number
  grand: string
  intermediates: Milestone[]
  intermediatesByStamp: Map<number, string>
  milestoneStamps: Set<number>
}

/**
 * The hero promise prepends a decorative "FREE" prefix unless the owner's
 * reward text already starts with "free" (any case). Prevents the cosmetically
 * embarrassing "FREE Free large Milk Tea" we hit at first run.
 */
function needsFreePrefix(text: string): boolean {
  return !/^\s*free\b/i.test(text)
}

/**
 * Pick black or white text for a colored background. Uses the perceptual
 * luminance formula (rec. 601 weights) instead of true sRGB linearization
 * — fine for sign-like UI text where we just need "is this background bright
 * or dim?". Falls back to dark text if the input isn't a parseable hex.
 */
function textOn(hex: string): string {
  const h = hex.replace('#', '')
  if (h.length !== 6) return '#1a1a1a'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  if (![r, g, b].every(Number.isFinite)) return '#1a1a1a'
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return lum > 0.6 ? '#1a1a1a' : '#ffffff'
}

function extractRewards(
  milestones: Milestone[],
  totalStamps: number,
): ExtractedRewards | null {
  const final = milestones.find((m) => m.type === 'final')
  if (!final) return null
  const intermediates = milestones
    .filter((m) => m.type === 'intermediate')
    .sort((a, b) => a.at_stamp - b.at_stamp)
  return {
    totalStamps,
    grand: final.reward_text,
    intermediates,
    intermediatesByStamp: new Map(
      intermediates.map((m) => [m.at_stamp, m.reward_text]),
    ),
    milestoneStamps: new Set(milestones.map((m) => m.at_stamp)),
  }
}

// Rasterizes any SVG node to a PNG and triggers a download. Used for both the
// QR (square, white fill) and the wallet button (transparent background).
function svgToPng(
  svg: SVGSVGElement | Element,
  width: number,
  height: number,
  filename: string,
  background: string | null,
) {
  const xml = new XMLSerializer().serializeToString(svg)
  const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' })
  const svgUrl = URL.createObjectURL(svgBlob)
  const img = new Image()
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      URL.revokeObjectURL(svgUrl)
      toast.error('Could not render the image.')
      return
    }
    if (background) {
      ctx.fillStyle = background
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    URL.revokeObjectURL(svgUrl)
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error('Could not render the image.')
        return
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    }, 'image/png')
  }
  img.onerror = () => {
    URL.revokeObjectURL(svgUrl)
    toast.error('Could not render the image.')
  }
  img.src = svgUrl
}
